import React, { useState } from 'react';
import { Modal, Input, Button, message } from 'antd';
import StateSelect from 'components/form/StateSelect';
import { suggestPayers } from 'api/credentialing';
import s from './s.module.less';

interface SuggestionRow {
    payer: string;
    state: string;
}

interface Props {
    open: boolean;
    states?: string[];
    onClose: () => void;
    onSubmitted?: (suggestions: { payerName: string; state: string; notes?: string }[]) => void;
}

const SuggestPayerModal = ({ open, states = [], onClose, onSubmitted }: Props) => {
    const isMultiState = states.length > 1;
    const defaultState = states[0] || '';

    const [rows, setRows] = useState<SuggestionRow[]>([{ payer: '', state: defaultState }]);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    // When the provider has no known states, only require a payer name (state is submitted empty).
    const canSubmit = rows.some((r) => r.payer.trim() && (r.state || states.length === 0));

    const resetFields = () => {
        setRows([{ payer: '', state: defaultState }]);
        setNote('');
    };

    const updateRow = (idx: number, field: keyof SuggestionRow, value: string) => {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    };

    const addRow = () => {
        setRows((prev) => [...prev, { payer: '', state: defaultState }]);
    };

    const removeRow = (idx: number) => {
        setRows((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async () => {
        if (!canSubmit || loading) return;
        setLoading(true);
        const suggestions = rows
            .filter((r) => r.payer.trim() && (r.state || states.length === 0))
            .map((r) => ({
                payerName: r.payer.trim(),
                state: r.state,
                notes: note.trim() || undefined,
            }));
        const res = await suggestPayers(suggestions);
        setLoading(false);
        if (res && !res.error) {
            message.success(
                suggestions.length > 1
                    ? `Your ${suggestions.length} suggestions have been submitted.`
                    : 'Your suggestion has been submitted.',
            );
            onSubmitted?.(suggestions);
            resetFields();
            onClose();
        }
    };

    const handleCancel = () => {
        resetFields();
        onClose();
    };

    return (
        <Modal
            open={open}
            title={null}
            footer={null}
            onCancel={handleCancel}
            destroyOnClose
            width={480}
            className={s.suggestModal}
        >
            <h3 className={s.suggestTitle}>Suggest a payer</h3>
            <p className={s.suggestDesc}>
                Don&apos;t see the payer you&apos;re looking for? Drop a suggestion and we&apos;ll consider adding it.
                This helps us decide which payers to add next. It won&apos;t start credentialing or add you to a
                waitlist.
            </p>

            <div className={s.fieldGroup}>
                <label className={s.fieldLabel}>{isMultiState ? 'Payer and state' : 'Payer name'}</label>
                {!isMultiState && states.length === 1 && (
                    <div className={s.fieldHint}>For your practice in {states[0]}.</div>
                )}
                {isMultiState && (
                    <div className={s.fieldHint}>You can suggest a different payer for each of your states.</div>
                )}
                <div className={s.rowList}>
                    {rows.map((row, idx) => (
                        <div key={idx} className={s.suggestionRow}>
                            <Input
                                placeholder="e.g. Tricare"
                                value={row.payer}
                                onChange={(e) => updateRow(idx, 'payer', e.target.value)}
                                size="large"
                                className={s.payerInput}
                            />
                            {isMultiState && (
                                <StateSelect
                                    value={row.state}
                                    onChange={(val) => updateRow(idx, 'state', val as string)}
                                    size="large"
                                    placeholder="State"
                                    className={s.stateSelect}
                                />
                            )}
                            {rows.length > 1 && (
                                <span className={s.removeRow} onClick={() => removeRow(idx)}>
                                    &times;
                                </span>
                            )}
                        </div>
                    ))}
                </div>
                <span className={s.addRow} onClick={addRow}>
                    + Suggest another payer
                </span>
            </div>

            <div className={s.fieldGroup}>
                <label className={s.fieldLabel}>Anything else?</label>
                <Input.TextArea
                    placeholder="Optional"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                />
            </div>

            <div className={s.suggestBtnRow}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="primary" disabled={!canSubmit} loading={loading} onClick={handleSubmit}>
                    Submit suggestion
                </Button>
            </div>
        </Modal>
    );
};

export default SuggestPayerModal;
